import * as yup from "yup";

/**
 * Shared Yup validation fragments for the account-facing forms
 * (Login, Forgot Password, Profile, User Management).
 *
 * Single source of truth so every form enforces identical rules and future
 * drift is prevented. Import these instead of redefining rules per schema.
 *
 * Canonical rules (PRD — Auth & User-Management Bug-Fix Batch):
 *  - Name : trim, 3–50 chars, ≥2 alphabetic characters (rejects all-numeric).
 *  - Mobile : exactly 10 digits (the `+91` prefix is display-only).
 *  - Email : must have a domain + TLD so `priya.v@vi` is rejected.
 */

// Require a domain with a TLD of ≥2 chars — stricter than yup.email().
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/**
 * Email — required by default.
 */
export const emailRule = ({ required = true } = {}) => {
  const base = yup
    .string()
    .trim()
    .matches(EMAIL_REGEX, { message: "Enter a valid email address", excludeEmptyString: true });
  return required ? base.required("Email is required") : base;
};

/**
 * Name (first / last / full) — required by default.
 * @param {string} label — used in the error copy ("First name", "Last name", …).
 */
export const nameRule = (label = "Name", { required = true, max = 50, min = 3 } = {}) => {
  const base = yup
    .string()
    .trim()
    .min(min, `${label} must be at least ${min} characters`)
    .max(max, `${label} must be at most ${max} characters`)
    .test("has-two-letters", `${label} must contain at least 2 letters`, (value) => {
      if (!value) return true; // emptiness handled by required()
      return (value.match(/[A-Za-z]/g) || []).length >= 2;
    });
  return required ? base.required(`${label} is required`) : base;
};

/**
 * Store code — 3–50 chars, letters and/or digits only (no spaces/symbols).
 * Value is auto-uppercased on blur by the form; the rule itself accepts any
 * case so validation does not race the transform.
 */
export const storeCodeRule = ({ required = true, label = "Store code" } = {}) => {
  let base = yup.string().trim();
  // When optional, treat blank as "absent" so the 3–50 / alnum rules don't
  // fire on an empty (auto-generated) code.
  if (!required) base = base.transform((v) => (v === "" ? undefined : v));
  base = base
    .min(3, `${label} must be at least 3 characters`)
    .max(50, `${label} must be at most 50 characters`)
    .matches(/^[A-Za-z0-9]+$/, `${label} may contain only letters and digits`);
  return required ? base.required(`${label} is required`) : base;
};

/**
 * Curated phone-prefix dropdown (PRD — UI/Validation batch, Store phone).
 * `+91` is the mobile path; the rest are major metro STD codes. The stored
 * value is the prefix + subscriber digits concatenated with no delimiter.
 */
export const PHONE_PREFIXES = [
  { value: "+91", label: "+91 (Mobile)", type: "mobile" },
  { value: "011", label: "011 (Delhi)", type: "landline" },
  { value: "022", label: "022 (Mumbai)", type: "landline" },
  { value: "033", label: "033 (Kolkata)", type: "landline" },
  { value: "044", label: "044 (Chennai)", type: "landline" },
  { value: "080", label: "080 (Bengaluru)", type: "landline" },
  { value: "040", label: "040 (Hyderabad)", type: "landline" },
  { value: "079", label: "079 (Ahmedabad)", type: "landline" },
  { value: "020", label: "020 (Pune)", type: "landline" },
];

const MOBILE_SUBSCRIBER_REGEX = /^[6-9]\d{9}$/;
const LANDLINE_SUBSCRIBER_REGEX = /^\d{6,8}$/;

/** Look up a prefix entry by its dial value (e.g. "+91", "022"). */
export const prefixType = (prefix) =>
  PHONE_PREFIXES.find((p) => p.value === prefix)?.type || "mobile";

/**
 * Split a stored, delimiter-less phone value back into { phonePrefix,
 * phoneNumber } for edit mode. Longest-prefix match against the curated
 * list; a legacy bare 10-digit value is restored as a `+91` mobile.
 */
export const splitStoredPhone = (stored) => {
  const def = { phonePrefix: "+91", phoneNumber: "" };
  if (!stored) return def;
  const s = String(stored).trim();
  if (s.startsWith("+91")) return { phonePrefix: "+91", phoneNumber: s.slice(3) };
  const landline = PHONE_PREFIXES.filter((p) => p.type === "landline").sort(
    (a, b) => b.value.length - a.value.length,
  );
  for (const p of landline) {
    if (s.startsWith(p.value))
      return { phonePrefix: p.value, phoneNumber: s.slice(p.value.length) };
  }
  return { phonePrefix: "+91", phoneNumber: s };
};

/** Compose the stored value from a prefix + subscriber digits (no delimiter). */
export const composePhone = (prefix, number) => {
  const n = (number || "").trim();
  if (!n) return null;
  return `${prefix}${n}`;
};

/**
 * Yup rule for the subscriber-digits field; branches on the sibling
 * `phonePrefix`. Optional (blank allowed) — when present it must match the
 * mobile (10 digits 6-9) or landline (6-8 digits) shape for the chosen prefix.
 */
export const phoneNumberRule = () =>
  yup
    .string()
    .trim()
    .transform((v) => (v === "" ? undefined : v))
    .when("phonePrefix", {
      is: "+91",
      then: (s) => s.matches(MOBILE_SUBSCRIBER_REGEX, "Mobile must be 10 digits starting 6-9"),
      otherwise: (s) => s.matches(LANDLINE_SUBSCRIBER_REGEX, "Landline number must be 6-8 digits"),
    });

/**
 * Mobile — exactly 10 digits. Optional by default (empty allowed); when
 * `required` it must be present and exactly 10 digits.
 */
export const mobileRule = ({ required = false } = {}) => {
  const pattern = required ? /^\d{10}$/ : /^(\d{10})?$/;
  // nullable + excludeEmptyString so an optional mobile accepts both "" (add
  // form) and null (edit form, where the backend returns null) — yup strings
  // are non-nullable by default and would otherwise reject null with
  // "mobile cannot be null".
  const base = yup.string().trim().nullable().matches(pattern, {
    message: "Enter a valid 10-digit mobile number",
    excludeEmptyString: true,
  });
  return required ? base.required("Mobile number is required") : base;
};
